import React, { useState } from "react";
import SwitchIcon from "src/assets/images/switch.svg";

interface QuantValidationPanelProps {
    json_response: any;
}

const QuantValidationPanel = ({ json_response }: QuantValidationPanelProps) => {
    const [open, setOpen] = useState<boolean>(true);

    const {
        alignment_table = [],
        routing_issues = [],
        anomalies = [],
        validation_status = ""
    } = json_response || {};

    const statusColor =
        validation_status === "clean"
            ? "text-green-700 bg-green-100 border-green-300"
            : "text-red-700 bg-red-100 border-red-300";

    return (
        <div
            className={`h-screen rounded-lg w-full overflow-auto pn_scroller transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px] p-2 border border-gray-300"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">

                {/* ───────────────── HEADER ───────────────── */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Alignment Table</h2>

                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                    >
                        <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
                    </button>
                </div>




                {/* ───────────────── ALIGNMENT TABLE ───────────────── */}
                <div className="mb-6">

                    {alignment_table.length === 0 ? (
                        <p className="text-sm text-gray-500">No alignment data found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg">
                                <thead>
                                    <tr className="bg-gray-100 text-left">
                                        <th className="p-2 border-b">Excel Question</th>
                                        <th className="p-2 border-b">Guide Question</th>
                                        <th className="p-2 border-b">Status</th>
                                        <th className="p-2 border-b">Confidence</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {alignment_table.map((row: any, i: number) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-2 py-2 border-none">{row.excel_q}</td>

                                            <td className="px-2 py-2 border-none">{row.guide_q}</td>

                                            <td className="px-2 py-2 border-none">
                                                <span
                                                    className={`px-2 rounded text-xs ${row.status === "matched"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"
                                                        }`}
                                                >
                                                    {row.status}
                                                </span>
                                            </td>

                                            <td className="px-2 py-2 border-none">
                                                {(row.match_confidence * 100).toFixed(0)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    )}
                </div>

                {/* ───────────────── VALIDATION STATUS ───────────────── */}
                <div className="mb-4">
                    <span
                        className={`inline-block px-3 py-1 rounded-md text-sm border font-medium capitalize ${statusColor}`}
                    >
                        Status: {validation_status}
                    </span>
                </div>

                {/* ───────────────── ROUTING ISSUES ───────────────── */}
                <div className="mb-6">
                    <h3 className="font-medium text-sm mb-2">Routing Issues</h3>

                    {routing_issues.length === 0 ? (
                        <p className="text-sm text-gray-500">No routing issues detected.</p>
                    ) : (
                        routing_issues.map((issue: string, i: number) => (
                            <div
                                key={i}
                                className="p-2 mb-2 border border-red-300 bg-red-50 rounded-md text-sm"
                            >
                                ⚠ {issue}
                            </div>
                        ))
                    )}
                </div>

                {/* ───────────────── ANOMALIES ───────────────── */}
                <div>
                    <h3 className="font-medium text-sm mb-2">Anomalies</h3>

                    {anomalies.length === 0 ? (
                        <p className="text-sm text-gray-500">No anomalies detected.</p>
                    ) : (
                        anomalies.map((anom: any, i: number) => (
                            <div
                                key={i}
                                className="p-3 border border-yellow-300 bg-yellow-50 rounded text-sm mb-2"
                            >
                                <p className="font-semibold text-yellow-800">
                                    📍 Location: {anom.location}
                                </p>
                                <p className="text-yellow-900 mt-1">
                                    ⚠ Issue: {anom.issue}
                                </p>
                            </div>
                        ))
                    )}
                </div>

            </div>
        </div>
    );
};

export default QuantValidationPanel;
